import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { appRoutes } from 'app/app-routes';
import { SnackVariant } from 'common/types/snack-notification';
import { UserActionCodeValues } from 'common/types/user-action-code';
import { UserActionCode, useUsersCheckVerifiedMutation, useUsersVerifyEmailMutation } from 'graphql/generated';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import WsPanel from 'stories/layout/WsPanel';

type RouteParams = Record<'email', string>;

const VerificationSent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { email } = useParams<RouteParams>();
  const [checkVerified] = useUsersCheckVerifiedMutation();
  const [verifyEmail] = useUsersVerifyEmailMutation();
  const [runOnce, setRunOnce] = useState(true);
  const [checkOnce, setCheckOnce] = useState(true);
  const user = useSelector((state: RootState) => cacheSelectors.user(state));

  const runCheck = useCallback(
    async (email: string) => {
      try {
        console.log('checking email', email);
        const { data } = await checkVerified({
          variables: { email },
        });
        if (!data) throw new Error('Register user error');
        const result = data.usersCheckVerified;
        if (result?.user && result.user.verified === true) {
          console.log('user is verified');
          const { __typename, ...user } = result.user;
          dispatch(cacheActions.user(user));
          navigate(appRoutes.home);
        } else setTimeout(() => runCheck(email), 5000);
      } catch (error: any) {
        dispatch(
          cacheActions.addSnack({
            message: error.message,
            variant: 'error',
          }),
        );
      }
    },
    [checkVerified, dispatch, navigate],
  );

  useEffect(() => {
    if (email && checkOnce) {
      try {
        setCheckOnce(false);
        setTimeout(() => runCheck(email), 5000);
      } catch (error: any) {
        console.log('error is', error.message);
        dispatch(
          cacheActions.addSnack({
            message: error.message,
            variant: 'error',
          }),
        );
      }
    }
  }, [email, runCheck, checkOnce, dispatch]);

  const sendHome = useCallback(
    (message: string, variant: SnackVariant) => {
      navigate(appRoutes.home);
      dispatch(cacheActions.addSnack({ message, variant }));
    },
    [dispatch, navigate],
  );

  const handleSendVerification = useCallback(
    async (snack: boolean) => {
      try {
        if (email) {
          if (user?.verified) {
            sendHome(UserActionCodeValues[UserActionCode.verified], 'success');
          } else {
            const { data } = await verifyEmail({
              variables: { email },
            });
            if (!data) throw new Error('Register user error');
            const result = data.usersVerifyEmail;
            if (result === UserActionCode.emailAlreadyVerified) {
              sendHome(UserActionCodeValues[UserActionCode.verified], 'success');
            } else if (result === UserActionCode.verificationCodeSent) {
              if (snack)
                dispatch(
                  cacheActions.addSnack({
                    message: UserActionCodeValues[UserActionCode.verificationCodeSent],
                    variant: 'success',
                  }),
                );
            } else throw new Error(UserActionCodeValues[result]);
          }
        }
      } catch (error: any) {
        dispatch(
          cacheActions.addSnack({
            message: error.message,
            variant: 'error',
          }),
        );
      }
    },
    [verifyEmail, email, sendHome, dispatch, user],
  );

  useEffect(() => {
    if (email) {
      if (runOnce) {
        setRunOnce(false);
        handleSendVerification(false);
      }
    } else navigate(appRoutes.errorPage);
  }, [email, handleSendVerification, navigate, runOnce]);

  return (
    <WsPanel width="440px" title="Email Verification">
      <Typography mb={1} variant="body1">{`An email has been sent to:`}</Typography>
      <Typography mb={1} pl={4} color="primary" variant="subtitle1">
        {user?.email}
      </Typography>
      <Typography variant="body1">
        {`Open the email and click on the link to verify your email address and activate your account.`}
      </Typography>
      <ul>
        <li>The email may take several minutes to reach your mailbox </li>
        <li>Be sure to check your spam folder </li>
        <li>
          If you do not receive the verification email within 30 minutes, click 'Send Again'. Any previous verification
          emails will no longer be valid.
        </li>
      </ul>
      <Button color="primary" variant="contained" fullWidth onClick={() => handleSendVerification(true)}>
        Send Again
      </Button>
    </WsPanel>
  );
};

export default VerificationSent;
